import { type FunctionComponent, useContext } from 'react';
import { ProductFamilyContext } from '../../../../../contexts/product-family/product-family.context';
import { ProductPricedOptionsContext } from '../../../../../contexts/product-priced-options/product-priced-options.context';
import { VariantContext } from '../../../../../contexts/variant/variant.context';
import { formatPrice } from '../../../../../helpers/general-helper/general-helper';
import { CloudinaryImage } from '../../../../images/cloudinary-image/cloudinary-image.component';
import { ProductPrice } from '../../../../product-components/product-price/product-price.component';
import CornerDownRightIcon from '../../../../svgs/untitled-ui/corner-down-right.svg';

type SummaryItemProps = {
	imageId: string;
	imageDescription: string;
	title: string;
	brandName: string;
	modelNumber: string;
	selectedFinishName: string;
	selectedVariantPrice: number;
	quantity: number;
	isSubItem: boolean;
	selectedOptions?: { label: string; value: string }[];
};

const SummaryItem: FunctionComponent<SummaryItemProps> = ({
	imageId,
	imageDescription,
	title,
	brandName,
	modelNumber,
	selectedFinishName,
	selectedVariantPrice,
	selectedOptions,
	quantity,
	isSubItem
}) => {
	return (
		<li className="flex mb2">
			{isSubItem && <CornerDownRightIcon className="theme-grey-light f3" data-testid="CornerDownRightIcon" />}
			<CloudinaryImage
				bypassLazyLoad={true}
				publicID={imageId}
				description={imageDescription}
				options={{ width: 90, height: 90 }}
				useDimensions={false}
				className="mr3"
				data-automation={`cart-drawer-summary-item-image-${title}`}
			/>
			<div>
				<span className="tc2-title theme-black mb1">
					<strong className="pr1 f-normal" data-automation={`cart-drawer-summary-item-brand-${title}`}>
						{brandName}
					</strong>
					{title}
				</span>
				<div className="f6 mb1" data-automation={`cart-drawer-summary-item-model-${title}`}>
					Model: {modelNumber}
				</div>
				<div className="f6 mb1">Finish: {selectedFinishName}</div>
				{selectedOptions && (
					<ul className="f6 mb1 pl3" data-automation={`cart-drawer-summary-item-selected-options-${title}`}>
						{selectedOptions
							.filter((option) => option.label && option.value)
							.map((option, index) =>
								option ? (
									<li
										key={option.value + index}
										data-automation={`cart-drawer-summary-item-selected-option-${option.value + index}`}>
										{option.label}: {option.value}
									</li>
								) : null
							)}
					</ul>
				)}
				<strong className="f-normal mb1">
					<ProductPrice data-automation={`cart-drawer-summary-item-price-${title}`} current={selectedVariantPrice * quantity} />
					Qty. {quantity} / {formatPrice(selectedVariantPrice)} each
				</strong>
			</div>
		</li>
	);
};

export const CartDrawerSummaryItems: FunctionComponent = () => {
	const { productFamily } = useContext(ProductFamilyContext);
	const { selectedVariant } = useContext(VariantContext);
	const { selectedOptions, subItems, textInputOptions, mainItemQuantity } = useContext(ProductPricedOptionsContext);
	const shapedSelectionOptions = selectedOptions
		.filter((option) => !option?.variantId)
		.map((option) => ({
			label: option?.groupName || '',
			value: option?.label || ''
		}));
	const shapedTextOptions = textInputOptions.map((option) => ({
		label: option?.label || '',
		value: option?.value || ''
	}));
	const combinedOptions = [...shapedSelectionOptions, ...shapedTextOptions];
	const additionalOptionPrice = selectedOptions.reduce((total, option) => total + (option?.price || 0), 0);

	return (
		<ul
			className="list flex flex-column w-100 ma0 pa0"
			id="cart-recently-updated-summary"
			data-testid="cart-items-recently-updated"
			data-automation="cart-drawer-summary-items"
			style={{ maxWidth: '32rem' }}>
			<SummaryItem
				imageId={selectedVariant.image.id}
				imageDescription={selectedVariant.image.description}
				title={productFamily.title}
				brandName={productFamily.brandInfo.name}
				modelNumber={selectedVariant.modelNumber}
				selectedFinishName={selectedVariant.title}
				selectedVariantPrice={selectedVariant.priceInfo.current + additionalOptionPrice}
				quantity={mainItemQuantity}
				isSubItem={false}
				selectedOptions={combinedOptions}
			/>
			{subItems &&
				subItems.length > 0 &&
				subItems.map((subItem) => (
					<SummaryItem
						key={subItem.variantId}
						imageId={subItem.imageId}
						imageDescription={`image of ${subItem.title}`}
						title={subItem.title}
						brandName={subItem.brandName}
						modelNumber={subItem.modelNumber}
						selectedFinishName={subItem.finish}
						selectedVariantPrice={subItem.price}
						quantity={subItem.quantity}
						isSubItem={true}
						selectedOptions={subItem.productOptions.map((option) => ({
							label: option?.groupName || '',
							value: option?.label || ''
						}))}
					/>
				))}
		</ul>
	);
};
